import { AfterViewInit, Component, DoCheck, OnInit } from '@angular/core';
import { GeneralFormComponent } from '../../controls/general-form/general-form.component';

// Debe reflejar los mismos codigos/nombres configurados como Items estaticos
// en el control UnidadMedida del JSON de la pagina ArchivoFisico (Fase 1: solo CAJA).
const UNIDADES_MEDIDA = [
  { Codigo: 'CAJA', Nombre: 'Cajas' },
  { Codigo: 'METRO_LINEAL', Nombre: 'Metros lineales' },
  { Codigo: 'CARPETA', Nombre: 'Carpetas' }
];

// Dimensiones referenciales (cm) por tipo de caja: Ancho x Alto x Profundidad.
const TIPOS_CAJA = [
  { Codigo: 'X100', Nombre: 'X-100', Ancho: 8, Alto: 30, Profundidad: 20 },
  { Codigo: 'X200', Nombre: 'X-200', Ancho: 10, Alto: 32, Profundidad: 25 },
  { Codigo: 'X300', Nombre: 'X-300', Ancho: 15, Alto: 32, Profundidad: 25 },
  { Codigo: 'SOBRECUBIERTA_LATERAL', Nombre: 'Sobrecubierta lateral', Ancho: 7, Alto: 32, Profundidad: 25 },
  { Codigo: 'MEDIANO_FORMATO', Nombre: 'Mediano formato', Ancho: 20, Alto: 35, Profundidad: 30 },
  { Codigo: 'GRAN_FORMATO', Nombre: 'Gran formato', Ancho: 30, Alto: 45, Profundidad: 35 }
];

@Component({
  selector: 'app-tarchivo-fisico',
  templateUrl: './tarchivo-fisico.component.html'
})
export class TArchivoFisicoComponent extends GeneralFormComponent implements OnInit, AfterViewInit, DoCheck {

  private ultimaFirmaCapacidad: string = null;

  public OnInitEnd() {

    if (!this.modelo.modeloVirtual) {
      this.modelo.modeloVirtual = {};
    }

    // Antes de guardar: consolida los campos virtuales de estructura en ConfiguracionCapacidad (JSON real del modelo)
    this.preSaveEmitter.subscribe(() => {
      this.ConsolidarConfiguracionCapacidad();
    });

    // Despues de cargar un registro para edicion: puebla los campos virtuales desde ConfiguracionCapacidad
    this.postEditEmitter.subscribe(() => {
      this.DeserializarConfiguracionCapacidad();
    });
  }

  // Recalculo reactivo en pantalla (solo vista previa); el backend siempre recalcula de forma autoritativa al guardar.
  ngDoCheck() {
    const v = this.modelo && this.modelo.modeloVirtual;
    if (!v) { return; }
    const firma = [
      v.NumeroEstanterias, v.CuerposPorEstanteria, v.NumeroEntrepanosUtiles,
      v.TipoCaja, v.Profundidad, v.AnchoUtilEntrepano, v.AlturaUtilEntrepanos
    ].join('|');
    if (firma !== this.ultimaFirmaCapacidad) {
      this.ultimaFirmaCapacidad = firma;
      this.RecalcularCapacidadTotal();
    }
  }

  // Cuantas cajas caben por entrepaño segun las medidas utiles y el tipo de caja (puede haber varias filas de profundidad).
  private ObtenerCapacidadPorEntrepano(): number {
    const v = this.modelo && this.modelo.modeloVirtual;
    if (!v) { return 0; }
    const caja = TIPOS_CAJA.find(x => x.Codigo === v.TipoCaja);
    if (!caja) { return 0; }

    const anchoUtil = Number(v.AnchoUtilEntrepano) || 0;
    const profundidad = Number(v.Profundidad) || 0;
    const alturaUtil = Number(v.AlturaUtilEntrepanos) || 0;

    if (alturaUtil > 0 && caja.Alto > alturaUtil) { return 0; } // la caja no cabe en la altura util del entrepaño

    const cajasPorFila = caja.Ancho > 0 ? Math.floor(anchoUtil / caja.Ancho) : 0;
    const filas = caja.Profundidad > 0 ? Math.floor(profundidad / caja.Profundidad) : 0;

    return Math.max(0, cajasPorFila) * Math.max(0, filas);
  }

  public RecalcularCapacidadTotal(): number {
    const v = this.modelo && this.modelo.modeloVirtual;
    if (!v || !this.modelo.modelo) { return 0; }
    const capacidadPorEntrepano = this.ObtenerCapacidadPorEntrepano();
    const total = (Number(v.NumeroEstanterias) || 0) *
      (Number(v.CuerposPorEstanteria) || 0) *
      (Number(v.NumeroEntrepanosUtiles) || 0) *
      capacidadPorEntrepano;
    this.modelo.modelo.CapacidadTotal = total;
    v.CapacidadPorEntrepano = capacidadPorEntrepano;
    this.ActualizarControlDeSoloLectura('CapacidadPorEntrepano', capacidadPorEntrepano);
    this.ActualizarControlDeSoloLectura('CapacidadTotal', total);
    return total;
  }

  // Los controles calculados son de solo lectura; sin este push el reactive form no refleja el recalculo en vivo.
  private ActualizarControlDeSoloLectura(nombre: string, valor: number) {
    const control = this.form && this.form.controls && this.form.controls[nombre];
    if (control) {
      control.setValue(valor, { emitEvent: false });
    }
  }

  private ConsolidarConfiguracionCapacidad() {
    const v = this.modelo.modeloVirtual || {};
    const capacidadPorEntrepano = this.ObtenerCapacidadPorEntrepano();
    this.RecalcularCapacidadTotal();

    const unidadMedida = UNIDADES_MEDIDA.find(x => x.Codigo === v.UnidadMedida);
    const tipoCaja = TIPOS_CAJA.find(x => x.Codigo === v.TipoCaja);

    const configuracionCapacidad = {
      numeroEstanterias: Number(v.NumeroEstanterias) || 0,
      cuerposPorEstanteria: Number(v.CuerposPorEstanteria) || 0,
      entrepanosPorCuerpo: Number(v.EntrepanosPorCuerpo) || 0,
      capacidadPorEntrepano: capacidadPorEntrepano,
      unidadMedida: unidadMedida ? { codigo: unidadMedida.Codigo, nombre: unidadMedida.Nombre } : null,
      tipoCaja: tipoCaja ? { codigo: tipoCaja.Codigo, nombre: tipoCaja.Nombre, ancho: tipoCaja.Ancho, alto: tipoCaja.Alto, profundidad: tipoCaja.Profundidad } : null,
      medidasEstanteria: {
        alturaTotal: Number(v.AlturaTotal) || 0,
        anchoPorCuerpo: Number(v.AnchoPorCuerpo) || 0,
        profundidad: Number(v.Profundidad) || 0,
        numeroEntrepanosUtiles: Number(v.NumeroEntrepanosUtiles) || 0,
        anchoUtilEntrepano: Number(v.AnchoUtilEntrepano) || 0,
        alturaUtilEntrepanos: Number(v.AlturaUtilEntrepanos) || 0
      },
      versionConfiguracion: 2
    };

    this.modelo.modelo.ConfiguracionCapacidad = JSON.stringify(configuracionCapacidad);
  }

  private DeserializarConfiguracionCapacidad() {
    const raw = this.modelo && this.modelo.modelo && this.modelo.modelo.ConfiguracionCapacidad;
    if (!raw) { return; }
    try {
      const config = (typeof raw === 'string') ? JSON.parse(raw) : raw;
      const medidas = config.medidasEstanteria || {};
      this.modelo.modeloVirtual.NumeroEstanterias = config.numeroEstanterias;
      this.modelo.modeloVirtual.CuerposPorEstanteria = config.cuerposPorEstanteria;
      this.modelo.modeloVirtual.EntrepanosPorCuerpo = config.entrepanosPorCuerpo;
      this.modelo.modeloVirtual.UnidadMedida = config.unidadMedida ? config.unidadMedida.codigo : null;
      this.modelo.modeloVirtual.TipoCaja = config.tipoCaja ? config.tipoCaja.codigo : null;
      this.modelo.modeloVirtual.AlturaTotal = medidas.alturaTotal;
      this.modelo.modeloVirtual.AnchoPorCuerpo = medidas.anchoPorCuerpo;
      this.modelo.modeloVirtual.Profundidad = medidas.profundidad;
      this.modelo.modeloVirtual.NumeroEntrepanosUtiles = medidas.numeroEntrepanosUtiles;
      this.modelo.modeloVirtual.AnchoUtilEntrepano = medidas.anchoUtilEntrepano;
      this.modelo.modeloVirtual.AlturaUtilEntrepanos = medidas.alturaUtilEntrepanos;
      this.RecalcularCapacidadTotal();
      this.SetModelToControls();
    }
    catch (error) {
      this.utility.logger.LogError(error, null, { componentName: this.componentName, method: 'DeserializarConfiguracionCapacidad' });
    }
  }
}

